import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import api from '../utils/axiosConfig';

const StatCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
}));

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [leads, setLeads] = useState([]);
  const [students, setStudents] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    fetchInquiries();
    fetchLeads();
    fetchStudents();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchInquiries = async () => {
    try {
      const response = await api.get('/inquiries');
      setInquiries(response.data.inquiries || []);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await api.get('/admin/leads');
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await api.get('/admin/students');
      setStudents(response.data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const handleInquiryClick = (inquiry) => {
    setSelectedInquiry(inquiry);
    setNotes(inquiry.notes || '');
    setStatus(inquiry.status || '');
    setDetailDialogOpen(true);
  };

  const handleUpdateInquiry = async () => {
    try {
      await api.patch(`/inquiries/${selectedInquiry._id}`, {
        status,
        notes,
      });
      setDetailDialogOpen(false);
      fetchInquiries();
      fetchLeads();
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating inquiry:', error);
      alert('Failed to update inquiry');
    }
  };

  const handleConvertLead = async (leadId) => {
    if (window.confirm('Convert this lead to a student?')) {
      try {
        await api.post(`/admin/leads/${leadId}/convert`);
        fetchLeads();
        fetchStudents();
        fetchDashboardData();
        alert('Lead converted to student successfully!');
      } catch (error) {
        console.error('Error converting lead:', error);
        alert('Failed to convert lead');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'info',
      contacted: 'warning',
      qualified: 'primary',
      converted: 'success',
      closed: 'default',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ py: 4, bgcolor: 'grey.100', minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mb: 4 }}>
          Admin Dashboard
        </Typography>

        {dashboardData && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent>
                  <Typography variant="h4" gutterBottom>
                    {dashboardData.statistics.totalInquiries}
                  </Typography>
                  <Typography variant="body1">Total Inquiries</Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent>
                  <Typography variant="h4" gutterBottom>
                    {dashboardData.statistics.newInquiries}
                  </Typography>
                  <Typography variant="body1">New Inquiries</Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent>
                  <Typography variant="h4" gutterBottom>
                    {dashboardData.statistics.totalLeads}
                  </Typography>
                  <Typography variant="body1">Total Leads</Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent>
                  <Typography variant="h4" gutterBottom>
                    {dashboardData.statistics.totalStudents}
                  </Typography>
                  <Typography variant="body1">Total Students</Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent>
                  <Typography variant="h4" gutterBottom>
                    {dashboardData.statistics.conversionRate}
                  </Typography>
                  <Typography variant="body1">Conversion Rate</Typography>
                </CardContent>
              </StatCard>
            </Grid>
          </Grid>
        )}

        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Inquiries" />
            <Tab label="Leads" />
            <Tab label="Students" />
          </Tabs>
        </Paper>

        {tabValue === 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inquiries.map((inquiry) => (
                  <TableRow key={inquiry._id} hover>
                    <TableCell>{inquiry.name}</TableCell>
                    <TableCell>{inquiry.email}</TableCell>
                    <TableCell>
                      <Chip label={inquiry.type} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={inquiry.status}
                        color={getStatusColor(inquiry.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(inquiry.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => handleInquiryClick(inquiry)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tabValue === 1 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Lead Status</TableCell>
                  <TableCell>Course</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead._id} hover>
                    <TableCell>{lead.name}</TableCell>
                    <TableCell>{lead.email}</TableCell>
                    <TableCell>
                      <Chip label={lead.leadStatus} size="small" />
                    </TableCell>
                    <TableCell>
                      {lead.courseName || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => handleInquiryClick(lead)}
                        sx={{ mr: 1 }}
                      >
                        View
                      </Button>
                      {!lead.convertedToStudent && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => handleConvertLead(lead._id)}
                        >
                          Convert
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tabValue === 2 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Courses Enrolled</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student) => (
                  <TableRow key={student._id} hover>
                    <TableCell>{student.name}</TableCell>
                    <TableCell>{student.email}</TableCell>
                    <TableCell>{student.phone}</TableCell>
                    <TableCell>{student.totalCourses}</TableCell>
                    <TableCell>{student.completedCourses}</TableCell>
                    <TableCell>
                      {new Date(student.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Inquiry Details</DialogTitle>
          <DialogContent>
            {selectedInquiry && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1" gutterBottom>{selectedInquiry.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1" gutterBottom>{selectedInquiry.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1" gutterBottom>{selectedInquiry.phone || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Company</Typography>
                    <Typography variant="body1" gutterBottom>{selectedInquiry.company || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Message</Typography>
                    <Typography variant="body1" gutterBottom>{selectedInquiry.message}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        label="Status"
                      >
                        <MenuItem value="new">New</MenuItem>
                        <MenuItem value="contacted">Contacted</MenuItem>
                        <MenuItem value="qualified">Qualified</MenuItem>
                        <MenuItem value="converted">Converted</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateInquiry} variant="contained">Update</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminDashboard;

